import { Action, Dispatch } from 'redux';
import i18next from 'i18next';

import {
  API_DEFAULT_RETRIES,
  COLLECTIONS,
  DRAWER_TYPES,
  MODAL_STATES,
  RequestId,
  TICKETS_URL,
} from 'appConstants';
import {
  AppState,
  deleteUpdateTicketDraft,
  NpsActions,
  postComment,
  refreshCollection,
  selectTicket,
  setModalStep,
  setTicketCheck,
  setTicketClosing,
  setTicketClosingError,
  setTicketClosingSuccess,
  startPolling,
  resetToastMessage,
  transformRecipients,
  updateResourceCollectionLocally,
  storeNPSMetrics,
  setNpstModalState,
  storeCESSurvey,
  showCesSurvey,
  setLoadingState,
  setSelectedRating,
  setSelectedFactor,
  setCesComment,
  setDrawerType,
} from 'reducers';
import {
  AccountUtils,
  ApiRequest,
  TicketUtils,
  UpdateDraft,
  FeatureFlags,
  MiscUtils,
  BrowserUtils,
} from 'services';
import {
  Account,
  CloudGroupUser,
  Recipient,
  SentryManualError,
  Ticket,
  TicketResource,
} from 'types';
import { isRacker } from '@rss-engineering/fabric-ui-session';

/* Types */

interface SelectedTicketAction extends Action {
  readonly account?: Account;
  readonly errorMsg?: string;
  readonly favorite?: boolean;
  readonly isDetachedView?: boolean;
  readonly isCommentUpdated?: boolean;
  readonly isRecipientsUpdated?: boolean;
  readonly recipient?: Recipient;
  readonly recipients?: Recipient[] | CloudGroupUser[];
  readonly ticket?: Ticket;
  readonly ticketId?: string;
  readonly visibility?: boolean;
  readonly processingFlag?: boolean;
  readonly isTicketClosedSucess?: boolean;
  readonly _isClosingError?: boolean;
  readonly currentClosedTicketId?: string;
  readonly retryFetchingDevices?: boolean;
  readonly errorFetchingDevices?: boolean;
}

/* Initial State */

const initialState = {} as Ticket;

/* Actions */

enum Actions {
  CLOSE_TICKET_ERROR = 'CLOSE_TICKET_ERROR',
  CLOSE_TICKET_START = 'CLOSE_TICKET_START',
  CLOSE_TICKET_SUCCESS = 'CLOSE_TICKET_SUCCESS',
  GET_TICKET_ERROR = 'GET_TICKET_ERROR',
  GET_TICKET_RETRY = 'GET_TICKET_RETRY',
  GET_TICKET_START = 'GET_TICKET_START',
  GET_TICKET_SUCCESS = 'GET_TICKET_SUCCESS',
  MARK_FOR_REMOVAL = 'MARK_FOR_REMOVAL',
  RESET_SELECTED_TICKET_STATE = 'RESET_SELECTED_TICKET_STATE',
  SET_CURRENT_RECIPIENTS = 'SET_CURRENT_RECIPIENTS',
  SET_DETACHED_VIEW = 'SET_DETACHED_VIEW',
  SET_TICKET_ACCOUNT = 'SET_TICKET_ACCOUNT',
  SET_COMMENT_UPDATED = 'SET_COMMENT_UPDATED',
  SET_RECIPIENTS_UPDATED = 'SET_RECIPIENTS_UPDATED',
  SET_PROCESSING_FLAG = 'SET_PROCESSING_FLAG',
  IS_TICKET_CLOSED_SUCCESS = 'IS_TICKET_CLOSED_SUCCESS',
  IS_TICKET_CLOSING_ERROR = ' IS_TICKET_CLOSING_ERROR',
  SET_TICKET_ID_GLOBAL = 'SET_TICKET_ID_GLOBAL',
  ERROR_FETCHING_DEVICES = 'ERROR_FETCHING_DEVICES',
  RETRY_FETCHING_DEVICES = 'RETRY_FETCHING_DEVICES',
}
const setCurrentClosedTicketId = (
  currentClosedTicketId: string,
): SelectedTicketAction => ({
  type: Actions.SET_TICKET_ID_GLOBAL,
  currentClosedTicketId,
});

const closeTicketError = (): SelectedTicketAction => ({
  type: Actions.CLOSE_TICKET_ERROR,
});

const closeTicketStart = (): SelectedTicketAction => ({
  type: Actions.CLOSE_TICKET_START,
});

const closeTicketSuccess = (): SelectedTicketAction => ({
  type: Actions.CLOSE_TICKET_SUCCESS,
});

const getTicketRetry = (retryNow: () => void, wait: number) => ({
  type: Actions.GET_TICKET_RETRY,
  retryNow,
  wait,
});

const getTicketStart = (ticketId: string): SelectedTicketAction => ({
  type: Actions.GET_TICKET_START,
  ticketId,
});

const getTicketSuccess = (ticket: Ticket): SelectedTicketAction => ({
  type: Actions.GET_TICKET_SUCCESS,
  ticket,
});

const markForRemoval = (recipient: Recipient): SelectedTicketAction => ({
  type: Actions.MARK_FOR_REMOVAL,
  recipient,
});

const resetSelectedState = (): SelectedTicketAction => ({
  type: Actions.RESET_SELECTED_TICKET_STATE,
});

const setCurrentRecipients = (
  recipients: Recipient[] | CloudGroupUser[],
): SelectedTicketAction => ({
  type: Actions.SET_CURRENT_RECIPIENTS,
  recipients,
});

const setDetachedView = (isDetachedView: boolean): SelectedTicketAction => ({
  type: Actions.SET_DETACHED_VIEW,
  isDetachedView,
});

const setCommentUpdated = (
  isCommentUpdated: boolean,
): SelectedTicketAction => ({
  type: Actions.SET_COMMENT_UPDATED,
  isCommentUpdated,
});

const setRecipientsUpdated = (
  isRecipientsUpdated: boolean,
): SelectedTicketAction => ({
  type: Actions.SET_RECIPIENTS_UPDATED,
  isRecipientsUpdated,
});

const setTicketAccount = (account: Account): SelectedTicketAction => ({
  type: Actions.SET_TICKET_ACCOUNT,
  account,
});

const setisTicketClosedSucess = (
  isTicketClosedSucess: boolean,
): SelectedTicketAction => ({
  type: Actions.IS_TICKET_CLOSED_SUCCESS,
  isTicketClosedSucess,
});

const setIsClosingError = (_isClosingError: boolean): SelectedTicketAction => ({
  type: Actions.IS_TICKET_CLOSING_ERROR,
  _isClosingError,
});

const setProcessingFlag = (processingFlag: boolean): SelectedTicketAction => ({
  type: Actions.SET_PROCESSING_FLAG,
  processingFlag,
});

const setErrorFetchingDevices = (
  errorFetchingDevices: boolean,
): SelectedTicketAction => ({
  type: Actions.ERROR_FETCHING_DEVICES,
  errorFetchingDevices,
});

const setRetryFetchingDevices = (
  retryFetchingDevices: boolean,
): SelectedTicketAction => ({
  type: Actions.RETRY_FETCHING_DEVICES,
  retryFetchingDevices,
});

/* Reducer */

const selectedTicketReducer = (
  state = initialState,
  action: SelectedTicketAction,
) => {
  switch (action.type) {
    /*
     * RESET_NPS action for clearing nps state
     * Listen to clear closing error in nps modal
     */
    case Actions.SET_TICKET_ID_GLOBAL:
      return {
        ...state,
        currentClosedTicketId: action.currentClosedTicketId,
      };
    case NpsActions.RESET_NPS:
      return {
        ...state,
        _isClosingError: false,
      };
    case Actions.CLOSE_TICKET_ERROR:
      return {
        ...state,
        _isClosingError: true,
        _isClosing: false,
      };
    case Actions.CLOSE_TICKET_SUCCESS:
      return {
        ...state,
        _isClosing: false,
        _isClosingError: false,
      };
    case Actions.CLOSE_TICKET_START:
      return {
        ...state,
        _isClosing: true,
      };
    case Actions.GET_TICKET_ERROR:
      return {
        ...state,
        ...({} as Ticket),
      };
    case Actions.GET_TICKET_START:
      return {
        ...state,
        ticketId: action.ticketId,
      };
    case Actions.GET_TICKET_SUCCESS:
      return {
        ...state,
        ...action.ticket,
      };
    case Actions.MARK_FOR_REMOVAL:
      if (
        action.recipient &&
        (action.recipient as Recipient)._isCloudTicketReviewer
      ) {
        return state;
      }
      const index = state.recipients!.indexOf(action.recipient as Recipient);
      const newRecipient = { ...action.recipient, _toRemove: true };
      return {
        ...state,
        recipients: Object.assign([], state.recipients, {
          [index]: newRecipient,
        }),
      };
    case Actions.RESET_SELECTED_TICKET_STATE:
      return { ...initialState };
    case Actions.SET_CURRENT_RECIPIENTS:
      return {
        ...state,
        recipients: action.recipients || [],
      };
    case Actions.SET_DETACHED_VIEW:
      return {
        ...state,
        isDetachedView: action.isDetachedView,
      };
    case Actions.SET_COMMENT_UPDATED:
      return {
        ...state,
        isCommentUpdated: action.isCommentUpdated,
      };
    case Actions.SET_RECIPIENTS_UPDATED:
      return {
        ...state,
        isRecipientsUpdated: action.isRecipientsUpdated,
      };
    case Actions.SET_TICKET_ACCOUNT:
      if (!action.account) {
        return state;
      }

      return {
        ...state,
        account: action.account,
        _isDisabled:
          state.status === i18next.t('common:closed') ||
          !TicketUtils.isAdmin(state),
      };
    case Actions.SET_PROCESSING_FLAG:
      return {
        ...state,
        meta: {
          ...state.meta,
          processing: action.processingFlag,
        },
      };
    case Actions.IS_TICKET_CLOSED_SUCCESS:
      return {
        ...state,
        isTicketClosedSucess: action.isTicketClosedSucess,
      };
    case Actions.IS_TICKET_CLOSING_ERROR:
      return {
        ...state,
        _isClosingError: action._isClosingError,
      };
    case Actions.ERROR_FETCHING_DEVICES:
      return {
        ...state,
        errorFetchingDevices: action.errorFetchingDevices,
      };
    case Actions.RETRY_FETCHING_DEVICES:
      return {
        ...state,
        retryFetchingDevices: action.retryFetchingDevices,
      };
    default:
      return state;
  }
};

/* Thunks */

var lastRequestId = '';

/**
 * Fetches Ticket from API, applying defaults and account transformations
 * Requires for Accounts to have been resolved on state.
 *
 * @param ticketId Id of Ticket to be fetched
 */
const getTicket = (ticketId: string, shouldUpdateTicketList: boolean) => (
  dispatch: Dispatch<SelectedTicketAction>,
  getState: Function,
) => {
  lastRequestId = ticketId;

  dispatch(getTicketStart(ticketId));

  const onRetry = (retryNow: () => void, wait: number) =>
    dispatch(getTicketRetry(retryNow, wait));

  const onSuccess = (ticket: Ticket) => {
    if (lastRequestId !== ticketId) {
      return;
    }

    dispatch(getTicketSuccess(ticket));

    const accounts = getState().accounts.items;
    const ticketAccount = AccountUtils.findById(accounts, ticket.accountId);
    if (shouldUpdateTicketList) {
      const ticketList = getState().Collections[COLLECTIONS.TICKET].view;
      const ticketDataDifference = TicketUtils.findTicketListAndDetailsDifference(
        ticket,
        ticketList,
        accounts.some(AccountUtils.isDedicated),
      );

      // If the selected ticket and the ticket details data is different
      if (ticketDataDifference.isDifferent) {
        // Get the changed values of selected ticket
        const updatedTicket = TicketUtils.populateChangedTicketList(
          ticketDataDifference.commonProperties,
          ticket,
        );

        // Create a new array of Ticket List with updates values
        let updatedTicketList = ticketList.map((currentTicket: Ticket) => {
          if (currentTicket.ticketId === ticket.ticketId) {
            return { ...currentTicket, ...updatedTicket };
          }
          return currentTicket;
        });

        // dispatch to update the Ticket List state
        dispatch(
          updateResourceCollectionLocally(
            updatedTicketList,
            COLLECTIONS.TICKET,
          ),
        );
      }
    }

    if (FeatureFlags.isActive('ticket-polling-on-update') && ticket.meta) {
      const ticketList = TicketUtils.getLocalStoragePollingDetails(
        window.PORTAL_DATA.domainId,
      );
      const ticketEventExist = ticketList.some(
        (localStorageTicket) => localStorageTicket.ticketId === ticket.ticketId,
      );
      /**
       * If processing meta flag is true,
       * then we need to start the polling
       */
      if (ticket.meta.processing || ticketEventExist) {
        dispatch(resetToastMessage());
        dispatch(startPolling());
      } else {
        /**
         * If processing meta flag is false and
         * there are no localstorage events for that ticket,
         * then we remove that particular ticket id from the
         * local storage polling ticket list
         */
        TicketUtils.removeTicketFromPollingList(ticket.ticketId);
      }
    }

    if (ticketAccount.id) {
      dispatch(setTicketAccount(ticketAccount));
    }

    // Log to sentry if no comments in ticket
    if (!ticket.comments || ticket.comments.length === 0) {
      MiscUtils.logSentryErrorManually({
        name: 'No Ticket Comment',
        description: `Ticket Id: ${ticket.ticketId}`,
        extraData: {
          permission: ticket.permission,
          modifiedDate: ticket.modified,
          ticketStatus: ticket.status,
        },
        fingerprint: [
          window.PORTAL_DATA.userId,
          MiscUtils.isUserImpersonated() ? 'customer' : 'racker',
          ticket.ticketId,
        ],
        tags: {
          userId: window.PORTAL_DATA.userId,
          domainId: window.PORTAL_DATA.domainId,
          isUserImpersonated: MiscUtils.isUserImpersonated(),
        },
      } as SentryManualError);
    }
  };

  const transform = (ticket: TicketResource) => {
    if (lastRequestId !== ticketId) {
      return ticket;
    }

    dispatch(transformRecipients(ticket));

    // Set defaults to ease empty checks in components
    return {
      ...ticket,
      recipients: [],
      resourceUris: ticket.resources || [],
    };
  };

  return ApiRequest.get(TICKETS_URL + `/${ticketId}`, {
    headers: {
      'x-show-meta': 'true',
      'x-async': 'true',
    },
    dispatchId: RequestId.GET_TICKET,
    onRetry,
    onSuccess,
    transform,
    retries: API_DEFAULT_RETRIES,
  });
};

const closeTicket = (
  ticketId: string,
  accountId: string,
  updateList: boolean,
  cesComment?: string,
  cesRating?: string,
  cesFactor?: Array<string>,
  surveyChecked?: boolean,
  npsComment?: string,
  score?: number,
  captureFeedbackToKevalin?: boolean,
) => async (dispatch: Dispatch<SelectedTicketAction>, getState: Function) => {
  let payload: {} = { status: 'Closed' };
  if (score !== undefined) {
    payload = {
      ...payload,
      rating: {
        score,
        comment: !npsComment && score < 7 ? 'No comment' : npsComment,
      },
    };
  }

  const onError = () => {
    dispatch(setIsClosingError(true));
    dispatch(setLoadingState(false));
    dispatch(closeTicketError());
  };

  const closeDrawer = () => {
    dispatch(setSelectedRating(''));
    dispatch(setSelectedFactor([]));
    dispatch(setCesComment(''));
    dispatch(setIsClosingError(false));
    dispatch(setLoadingState(false));
    dispatch(setDrawerType(DRAWER_TYPES.NONE));
  };

  const onSuccess = async () => {
    if (captureFeedbackToKevalin) {
      await dispatch(storeNPSMetrics([ticketId], score, npsComment));
    }

    dispatch(setTicketCheck(ticketId, false));
    if (!isRacker()) {
      dispatch(setLoadingState(false));
      dispatch(
        storeCESSurvey(
          ticketId,
          accountId,
          cesFactor!,
          cesRating!,
          cesComment!,
        ),
      );
      dispatch(showCesSurvey(false));
      closeDrawer();
      dispatch(setisTicketClosedSucess(true));
    }
    dispatch(closeTicketSuccess());
    const { nps } = getState() as AppState;
    if (nps._isNpstVisible) {
      dispatch(setNpstModalState(true));
    }
    if (
      !FeatureFlags.isActive('ctui-responsive') ||
      !BrowserUtils.isScreenSize768()
    ) {
      if (updateList) {
        await dispatch(refreshCollection(COLLECTIONS.TICKET));
        const ticketCollection = getState().Collections[COLLECTIONS.TICKET];
        dispatch(
          selectTicket(
            ticketCollection.view.length
              ? ticketCollection.view[0].ticketId
              : ticketId,
          ),
        );
      } else {
        dispatch(getTicket(ticketId, true));
      }
    } else {
      await dispatch(refreshCollection(COLLECTIONS.TICKET));
    }
  };

  dispatch(closeTicketStart());

  await ApiRequest.put(TICKETS_URL + `/${ticketId}`, payload, {
    onError,
    onSuccess,
  });
};

const closeCurrentTicketAndUpdate = (
  cesComment?: string,
  cesRating?: string,
  cesFactor?: Array<string>,
  surveyChecked?: boolean,
) => async (dispatch: Dispatch<SelectedTicketAction>, getState: Function) => {
  const { newComment, selectedTicket } = getState() as AppState;
  // Must Post comment before closing a ticket
  if (newComment.message.trim().length) {
    await dispatch(postComment(selectedTicket.ticketId, newComment.message));
  }
  const { newComment: updatedComment } = getState() as AppState;

  if (!updatedComment.errorMsg) {
    dispatch(setTicketClosing());
    await dispatch(
      closeTicket(
        selectedTicket.ticketId,
        selectedTicket.accountId,
        !selectedTicket.isDetachedView,
        cesComment,
        cesRating,
        cesFactor,
        surveyChecked,
      ),
    );
    const {
      selectedTicket: updatedTicket,
      portalPreferences: { updateTicketDraft },
    } = getState() as AppState;

    if (updatedTicket._isClosingError) {
      dispatch(setModalStep(MODAL_STATES.ERROR));
      dispatch(setTicketClosingError(updatedTicket.ticketId));
    } else {
      if (
        UpdateDraft.checkForUpdateTicketDraft(
          selectedTicket.ticketId,
          updateTicketDraft,
        )
      ) {
        dispatch(deleteUpdateTicketDraft(selectedTicket.ticketId));
      }
      dispatch(setTicketClosingSuccess(updatedTicket.ticketId));
      dispatch(setModalStep(MODAL_STATES.SUCCESS));
    }
  } else {
    dispatch(setModalStep(MODAL_STATES.ERROR));
    dispatch(setTicketClosingError(selectedTicket.ticketId));
  }
};

export {
  closeTicket,
  closeCurrentTicketAndUpdate,
  getTicket,
  markForRemoval,
  resetSelectedState,
  selectedTicketReducer,
  setCurrentRecipients,
  setDetachedView,
  setCommentUpdated,
  setRecipientsUpdated,
  setTicketAccount,
  setProcessingFlag,
  getTicketSuccess,
  setisTicketClosedSucess,
  setIsClosingError,
  setCurrentClosedTicketId,
  setErrorFetchingDevices,
  setRetryFetchingDevices,
};
